import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import { AuthQRCodeModel, CredenciaisModel } from 'src/app/shared/models/auth.model';
import { UsuarioModel } from 'src/app/shared/models/usuario.model';
import { environment } from 'src/environments/environment';
import { StorageService } from '../behavior/storage.service';
import { ResultDTO } from 'src/app/shared/models/resultDTO.model';
import { Device } from '@capacitor/device';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    public isAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

    public numeroCaixa: number;
    public baseUrl: string = '';
    public databaseName: string = '';
    public isAutoAtendimento: boolean = false;
    public token: string = '';
    public nomeTerminal: string = '';
    public baseUrlQrCode: string = 'https://cardapio.mastervariedades.com.br';
    public dispositivoId: string = '';
    public solicitarSenhaSomentePrimeiraVez: boolean = false;

    user: UsuarioModel;

    constructor(
        private http: HttpClient,
        private router: Router,
        private storageService: StorageService,
    ) {
        this.load();
    }

    login(credentials: CrediciaisRequest, skipSuccessInterceptor = false): Observable<{ token: string, usuario: UsuarioModel, nomeTerminal: string }> {
        let headers = skipSuccessInterceptor ? new HttpHeaders({ 'X-Skip-Success-Interceptor': 'true' }) : new HttpHeaders();

        if (credentials.clientid) {
            headers = headers.set('clientid', credentials.clientid);
        }

        return this.http.post<{ token: string, usuario: UsuarioModel, nomeTerminal: string }>(`${this.baseUrl}/Auth/loginEmpresa`, credentials, { headers }).pipe(
            first(),
        );
    }

    loginSomenteComSenha(senha: string): Observable<ResultDTO> {
        const headers = new HttpHeaders({ 'X-Skip-Success-Interceptor': 'true', 'X-Skip-Loading-Interceptor': 'true' });
        const model = { login: '', password: senha }

        return this.http.post<ResultDTO>(`${this.baseUrl}/Auth/loginUsuarioSomenteSenha`, model, { headers }).pipe(
            first(),
        )
    }

    async logout({ removeDatabaseName = true, removeBaseUrl = true } = {}): Promise<void> {
        this.isAuthenticated.next(false);

        if (removeDatabaseName)
            this.databaseName = '';

        if (removeBaseUrl)
            this.baseUrl = '';

        this.token = '';
        this.isAutoAtendimento = false;

        this.router.navigateByUrl('/login', { replaceUrl: true });

        // await this.storageService.clear();
        await this.storageService.remove('token');

        await this.storageService.set(environment.credentialsKeys.numeroCaixa, this.numeroCaixa);

        if (!removeDatabaseName)
            await this.storageService.set(environment.credentialsKeys.databaseName, this.databaseName);

        if (!removeBaseUrl)
            await this.storageService.set(environment.credentialsKeys.baseUrl, this.baseUrl);
    }

    async set(data: CredenciaisModel, isAutoAtendimento: boolean = false): Promise<void> {
        this.baseUrl = data.baseUrl;
        this.databaseName = data.databaseName;
        this.isAutoAtendimento = isAutoAtendimento;
        this.token = data.token;
        this.user = data.usuario;
        this.numeroCaixa = data.numeroCaixa;

        await Promise.all([
            this.storageService.set(environment.credentialsKeys.databaseName, data.databaseName),
            this.storageService.set(environment.credentialsKeys.isAutoAtendimento, isAutoAtendimento),
            this.storageService.set(environment.credentialsKeys.numeroCaixa, data.numeroCaixa),
        ]);

        if (!isAutoAtendimento) {
            await Promise.all([
                this.storageService.set(environment.credentialsKeys.token, data.token),
                this.storageService.set(environment.credentialsKeys.user, data.usuario),
                this.storageService.set(environment.credentialsKeys.baseUrl, data.baseUrl),
                this.setDataAutenticacao()
            ]);

            this.isAuthenticated.next(true);
        }
    }

    public setAuth(auth: AuthQRCodeModel) {        
        this.baseUrl = auth.baseUrl;
        this.databaseName = auth.databaseName;
        this.numeroCaixa = auth.numeroCaixa;
        this.solicitarSenhaSomentePrimeiraVez = auth.solicitarSenhaSomentePrimeiraVez;
        this.baseUrlQrCode = auth.baseUrlQrCode;
        this.nomeTerminal = auth.nomeTerminal;

        this.salvarAuth(auth);
    }

    public async salvarAuth(auth: AuthQRCodeModel) {        
        await Promise.all([
            this.storageService.set(environment.credentialsKeys.baseUrl, auth.baseUrl),
            this.storageService.set(environment.credentialsKeys.databaseName, auth.databaseName),
            this.storageService.set(environment.credentialsKeys.numeroCaixa, auth.numeroCaixa),
            this.storageService.set(environment.solicitarSenhaSomentePrimeiraVez, auth.solicitarSenhaSomentePrimeiraVez),
            this.storageService.set(environment.credentialsKeys.nomeTerminal, auth.nomeTerminal),
            this.storageService.set(environment.baseUrlQrCode, auth.baseUrlQrCode),
        ]);
    }

    public async reload() {
        await this.load();
    }

    private async load(): Promise<void> {
        const [token, databaseName, isAutoAtendimento, baseUrl, user, numeroCaixa, nomeTerminal, dispositivoId, baseUrlQrCode] = await Promise.all([
            this.storageService.get(environment.credentialsKeys.token),
            this.storageService.get(environment.credentialsKeys.databaseName),
            this.storageService.get(environment.credentialsKeys.isAutoAtendimento),
            this.storageService.get(environment.credentialsKeys.baseUrl),
            this.storageService.get(environment.credentialsKeys.user),
            this.storageService.get(environment.credentialsKeys.numeroCaixa),
            this.storageService.get(environment.credentialsKeys.nomeTerminal),
            this.storageService.get(environment.credentialsKeys.dispositivoId),
            this.storageService.get(environment.baseUrlQrCode),
        ]);

        if (databaseName)
            this.databaseName = databaseName;

        if (isAutoAtendimento)
            this.isAutoAtendimento = isAutoAtendimento;

        if (baseUrl === '') {
            throw new Error('baseUrl vazia');
        }

        if (baseUrl)
            this.baseUrl = baseUrl;

        if (user)
            this.user = user;
        
        if (numeroCaixa)
            this.numeroCaixa = numeroCaixa;

        if (token) {
            this.token = token;
            this.isAuthenticated.next(true);
        } else {
            this.isAuthenticated.next(false);
        }
                
        if (baseUrlQrCode)
            this.baseUrlQrCode = baseUrlQrCode;

        if (nomeTerminal)
            this.nomeTerminal = nomeTerminal;

        if (dispositivoId)
            this.dispositivoId = dispositivoId;
    }

    private async setDataAutenticacao(): Promise<void> {
        const date: Date = new Date();
        await this.storageService.set(environment.credentialsKeys.dataAutenticacao, date);
    }

    public async obterDeviceId(): Promise<string> {
        if (this.dispositivoId != '') {
            return this.dispositivoId;
        }

        const deviceId = (await Device.getId()).identifier;
        this.setDeviceId(deviceId);

        return deviceId;
    }

    public async setDeviceId(dispositivoId: string) {
        this.dispositivoId = dispositivoId;
        await this.storageService.set(environment.credentialsKeys.dispositivoId, dispositivoId);
    }
}

export class CrediciaisRequest {
    login: string;
    password: string;
    whatsappResponsavel: string;
    whatsappEmpresa: string;
    tokenMFA: string;
    clientid?: string;
}